/**
 * Workspace store - tenant and project selection with persistence
 * Task 27: Updated for tenant configuration support
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Tenant, Project } from '../types/api';
import { TenantDeploymentConfig } from '../types/tenant-config';

interface WorkspaceState {
  // Current selection
  tenantId: string | null;
  projectId: string | null;
  tenant: Tenant | null;
  project: Project | null;
  
  // Tenant configuration (Phase 3)
  tenantConfig: TenantDeploymentConfig | null;
  tenantConfigLoading: boolean;
  tenantConfigError: string | null;
  
  // Available options
  tenants: Tenant[];
  projects: Project[];
  
  // Actions
  setTenant: (tenant: Tenant) => void;
  setProject: (project: Project) => void;
  setProjectId: (projectId: string) => void;
  setTenants: (tenants: Tenant[]) => void;
  setProjects: (projects: Project[]) => void;
  setTenantConfig: (config: TenantDeploymentConfig | null) => void;
  setTenantConfigLoading: (loading: boolean) => void;
  setTenantConfigError: (error: string | null) => void;
  clearWorkspace: () => void;
}

export const useWorkspaceStore = create<WorkspaceState>()(
  persist(
    (set, get) => ({
      tenantId: null,
      projectId: null,
      tenant: null,
      project: null,
      tenantConfig: null,
      tenantConfigLoading: false,
      tenantConfigError: null,
      tenants: [],
      projects: [],
      
      setTenant: (tenant) => set({ 
        tenant, 
        tenantId: tenant.id, 
        project: null, 
        projectId: null,
        tenantConfig: null,  // Clear config when tenant changes
        tenantConfigError: null,
      }),
      
      setProject: (project) => set({ project, projectId: project.id }),
      
      setProjectId: (projectId) => {
        const state = get();
        const project = state.projects.find(p => p.id === projectId);
        if (project) {
          set({ project, projectId: project.id });
        }
      },
      
      setTenants: (tenants) => set({ tenants }),
      
      setProjects: (projects) => set({ projects }),
      
      setTenantConfig: (config) => set({ tenantConfig: config, tenantConfigLoading: false }),
      
      setTenantConfigLoading: (loading) => set({ tenantConfigLoading: loading }),
      
      setTenantConfigError: (error) => set({ tenantConfigError: error, tenantConfigLoading: false }),
      
      clearWorkspace: () => set({
        tenantId: null,
        projectId: null,
        tenant: null,
        project: null,
        projects: [],
        tenantConfig: null,
        tenantConfigLoading: false,
        tenantConfigError: null,
      }),
    }),
    {
      name: 'tracekit-workspace',
      partialize: (state) => ({
        tenantId: state.tenantId,
        projectId: state.projectId,
      }),
    }
  )
);


